import React, { useMemo, useRef } from 'react';
import {
  Animated,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { jobsFromAgent } from '@/utils/agent_work_suggestions';

function AgentWorkSuggestions({
  agent,
  onSelect,
  inputValue = '',
  isMobile = false,
  animateOnMobile = false,
}) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(isMobile), [isMobile]);
  const suggestionsAnim = useRef(new Animated.Value(animateOnMobile ? 0 : 1)).current;

  const jobs = useMemo(() => jobsFromAgent(agent), [agent]);

  React.useEffect(() => {
    if (!animateOnMobile) return;
    Animated.spring(suggestionsAnim, {
      toValue: inputValue.trim() ? 0 : 1,
      tension: 50,
      friction: 7,
      useNativeDriver: true,
    }).start();
  }, [animateOnMobile, inputValue, suggestionsAnim]);

  const animatedStyle = animateOnMobile ? {
    opacity: suggestionsAnim,
    transform: [{
      translateY: suggestionsAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [80, 0],
      }),
    }],
    pointerEvents: inputValue.trim() ? 'none' : 'auto',
  } : {};

  if (!jobs.length) return null;

  return (
    <Animated.View style={[styles.container, animatedStyle]}>
      <View style={styles.jobsBlock}>
        <Text size="xxs" weight="bold" color="tertiary" style={styles.eyebrow}>
          {t('pages.agents.jobsEyebrow') || 'Start with a job'}
        </Text>
        <ScrollView
          horizontal={isMobile}
          showsHorizontalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={isMobile ? styles.jobsCarousel : styles.jobsGrid}
        >
          {jobs.map((job) => (
            <Pressable
              key={job.id}
              onPress={() => onSelect?.(job.prompt)}
              accessibilityRole="button"
              accessibilityLabel={job.label}
              accessibilityHint="Adds this job to the composer"
              style={({ hovered, pressed }) => [
                styles.job,
                isMobile && styles.jobMobile,
                {
                  borderColor: colors.border_color_secondary,
                  backgroundColor: hovered || pressed
                    ? colors.solid_color_secondary
                    : colors.solid_color_primary,
                },
              ]}
            >
              <View style={[styles.jobIcon, { backgroundColor: colors.solid_color_secondary }]}>
                <Icon name={job.icon} size={15} color={colors.text_color_secondary} />
              </View>
              <View style={styles.jobCopy}>
                <Text size="s" weight="semibold" numberOfLines={1}>{job.label}</Text>
                {!!job.description && (
                  <Text size="xxs" color="secondary" numberOfLines={2} style={styles.jobDescription}>
                    {job.description}
                  </Text>
                )}
              </View>
              <Icon name="arrow-right" size={11} color={colors.text_color_tertiary} />
            </Pressable>
          ))}
        </ScrollView>
      </View>
    </Animated.View>
  );
}

AgentWorkSuggestions.propTypes = {
  agent: PropTypes.object,
  onSelect: PropTypes.func,
  inputValue: PropTypes.string,
  isMobile: PropTypes.bool,
  animateOnMobile: PropTypes.bool,
};

const createStyles = (isMobile) => StyleSheet.create({
  container: {
    width: '100%',
    gap: 16,
    marginTop: 4,
  },
  eyebrow: {
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  jobsBlock: {
    width: '100%',
    gap: 8,
  },
  jobsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    width: '100%',
  },
  jobsCarousel: {
    flexDirection: 'row',
    gap: 8,
    paddingRight: 12,
  },
  job: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flexGrow: 1,
    flexBasis: isMobile ? 240 : 220,
    minHeight: 76,
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 11,
    ...Platform.select({ web: { cursor: 'pointer' } }),
  },
  jobMobile: {
    flexGrow: 0,
    flexShrink: 0,
    width: 260,
  },
  jobIcon: {
    width: 34,
    height: 34,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  jobCopy: {
    flex: 1,
    minWidth: 0,
  },
  jobDescription: {
    marginTop: 2,
    lineHeight: 16,
  },
});

export default React.memo(AgentWorkSuggestions);
